import { BaseModel, BaseWithId } from './base';
import { Paginated } from './pagination';

export class BaseList<T extends BaseModel> {

    public items: T[];

    get hasItems(): boolean {
        return this.items && this.items.length > 0;
    }

    constructor(public type?: (new () => T) | ((json?: any) => (new (json?: any) => T)), json?: any) {
        this.items = new Array<T>();
        if (json) {
            this.initialize(json);
        }
    }

    public initialize(json?: any): void {
        if (json && json instanceof Array) {
            this.items = json.map((jsonItem: any) => this.createItemByClass(jsonItem));
        }
    }

    protected createItemByClass(json: any): T {
        const constructorX = this.getConstructor(this.type, json);
        const newObj = new constructorX();
        newObj.initialize(json);
        return newObj as T;
    }

    public addItem(item: T, inFront?: boolean) {
        if (inFront) {
            this.items.unshift(item);
        } else {
            this.items.push(item);
        }
    }

    public removeItem(item: T) {
        const idx = this.items.indexOf(item);
        if (idx > -1) {
            this.items.splice(idx, 1);
        }
    }

    protected getConstructor<X extends BaseModel>(constructorOrConstructorFactory, json?: any): (new (json?: any) => X) {
        try {
            // Test if we can call the param as a constructor
            // If no errors we return it as it is the constructor we are looking for
            const test = new constructorOrConstructorFactory();
            return constructorOrConstructorFactory;
        } catch (err) {
            // If an error occurs then we are dealing with the constructor factory
            // and we call the factory method with the provided json to determine the correct subclassed constructor.
            return constructorOrConstructorFactory(json);
        }
    }
}

export class BaseWithIdList<T extends BaseWithId> extends BaseList<T> {

    public items: T[];

    constructor(public type?: (new () => T) | ((json?: any) => (new (json?: any) => T)), json?: any) {
        super(type, json);
    }

    public static upgradeFromBaseList<T extends BaseWithId>(baseList: BaseList<T>): BaseWithIdList<T> {
        const baseWithIdList = new BaseWithIdList(baseList.type);
        baseWithIdList.items = baseList.items;
        return baseWithIdList;
    }

    public removeItem(item: T) {
        this.items = this.items.filter(i => i.id !== item.id);
    }

    /**
     * @param id
     */
    public getItem(id: string): T | null {
        return this.items.find(i => i.id === id);
    }

    public replaceItem(item: T): void {
        if (item) {
            const foundItem = this.getItem(item.id);
            const idx = this.items.indexOf(foundItem);
            this.items[idx] = item;
        }
    }
}

export class PaginatedList<T extends BaseWithId> extends BaseList<T> implements Paginated {
    public from: number;
    public limit: number;
    public row_count: number;

    constructor(public type?: new () => T, json?: any, arrayProperty?: string) {
        super(type, json ? (arrayProperty ? json[arrayProperty] : []) : []);
    }

    public initialize(json: any): void {
        super.initialize(json);
        this.from = json.from;
        this.limit = json.limit;
        this.row_count = json.row_count;
    }
}
